import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Globe } from "lucide-react";

const LINKS = {
  platform: [
    { to: "/explore", label: "Explorer" },
    { to: "/artists", label: "Artistes" },
    { to: "/castings", label: "Appel à candidatures" },
    { to: "/premium", label: "Premium" },
    { to: "/sondages", label: "Sondages" },
  ],
  pour_artistes: [
    { to: "/upload", label: "Publier un morceau" },
    { to: "/register", label: "Créer un compte" },
    { to: "/about", label: "À propos" },
    { to: "/partners", label: "Devenir partenaire" },
  ],
  legal: [
    { to: "/annoncer", label: "Annoncer chez nous" },
    { to: "/confidentialite", label: "Confidentialité" },
    { to: "/cgu", label: "Conditions Générales" },
    { to: "/cookies", label: "Cookies" },
    { to: "/mentions-legales", label: "Mentions légales" },
    { href: "mailto:contact@voamada.com", label: "Nous contacter" },
  ],
};

export const Footer = () => (
  <footer
    className="relative mt-20 border-t border-white/5 bg-[#0A0807] px-6 md:px-12 py-14"
    data-testid="app-footer"
  >
    <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-5">
      {/* Brand column */}
      <div className="md:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <img src="/assets/icon-192.png" alt="" width="44" height="44" className="rounded-xl" />
          <div>
            <p className="font-display font-black text-xl leading-none">
              <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
                Voamada
              </span>
            </p>
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mt-1">
              La graine qui fait pousser les talents
            </p>
          </div>
        </div>
        <p className="text-sm text-white/55 max-w-sm leading-relaxed">
          Plateforme dédiée aux talents malgaches et africains. Musique, humour,
          cinéma, danse, art visuel, mode — un tremplin unique pour faire
          rayonner la scène créative du continent.
        </p>
        <div className="flex items-center gap-2 mt-5">
          {[
            { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
            { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
            { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
            { icon: Globe, href: "https://www.voamada.com", label: "Site" },
            { icon: Mail, href: "mailto:contact@voamada.com", label: "Email" },
          ].map(({ icon: Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-brand-orange/50 hover:bg-white/5 transition-colors"
              data-testid={`footer-social-${label.toLowerCase()}`}
            >
              <Icon size={15} />
            </a>
          ))}
        </div>
      </div>

      {/* Link columns */}
      <FooterColumn title="Plateforme" items={LINKS.platform} />
      <FooterColumn title="Artistes" items={LINKS.pour_artistes} />
      <FooterColumn title="Plus" items={LINKS.legal} />
    </div>

    <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 text-xs text-white/40">
      <p>© {new Date().getFullYear()} Voamada. Tous droits réservés.</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("voamada:open-cookie-prefs"))}
          className="hover:text-white transition-colors"
          data-testid="footer-manage-cookies"
        >
          🍪 Gérer mes cookies
        </button>
        <span className="text-white/20">·</span>
        <p className="flex items-center gap-2">
          Fait avec 🌱 à Antananarivo <span className="text-white/20">·</span> Pour l'Afrique
        </p>
      </div>
    </div>

    {/* Propulsé par Top Mada */}
    <div className="max-w-7xl mx-auto mt-6 flex justify-center" data-testid="footer-poweredby-topmada">
      <a
        href="https://www.voamada.com"
        className="group inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white/[0.02] border border-white/5 hover:border-amber-400/30 hover:bg-amber-400/[0.04] transition"
        aria-label="Propulsé par Top Mada"
      >
        <img
          src="/assets/topmada-logo.png"
          alt="Top Mada"
          className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-400/30 group-hover:ring-amber-400/60 transition shadow-lg shadow-amber-500/10"
          loading="lazy"
        />
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45 group-hover:text-white/70 transition flex flex-col leading-tight">
          <span className="text-white/35 group-hover:text-white/55">Propulsé par</span>
          <span className="text-base font-black tracking-tight normal-case bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
            Top Mada
          </span>
        </span>
      </a>
    </div>
  </footer>
);

const FooterColumn = ({ title, items }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-4">{title}</p>
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it.label}>
          {it.href ? (
            <a
              href={it.href}
              className="text-sm text-white/65 hover:text-white transition-colors"
            >
              {it.label}
            </a>
          ) : (
            <Link
              to={it.to}
              className="text-sm text-white/65 hover:text-white transition-colors"
            >
              {it.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
